import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

/**
 * Export data to Excel file
 * @param data Array of objects to export
 * @param filename Base filename without extension
 * @param sheetName Sheet name in the Excel file
 */
export function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  sheetName: string = "Sheet1"
): void {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Create worksheet from data
  const ws = XLSX.utils.json_to_sheet(data);

  // Create workbook and add the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Generate filename with date
  const dateStr = format(new Date(), "yyyy-MM-dd");
  const fullFilename = `${filename}_${dateStr}.xlsx`;

  // Write and download the file
  XLSX.writeFile(wb, fullFilename);
}

/**
 * Export data with custom column headers
 * @param data Array of objects to export
 * @param headers Object mapping original keys to display headers
 * @param filename Base filename without extension
 * @param sheetName Sheet name in the Excel file
 */
export function exportToExcelWithHeaders<T extends Record<string, unknown>>(
  data: T[],
  headers: Record<string, string>,
  filename: string,
  sheetName: string = "Sheet1"
): void {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Transform data to use custom headers
  const transformedData = data.map((row) => {
    const newRow: Record<string, unknown> = {};
    for (const key of Object.keys(row)) {
      const headerName = headers[key] || key;
      newRow[headerName] = row[key];
    }
    return newRow;
  });

  // Create worksheet from transformed data
  const ws = XLSX.utils.json_to_sheet(transformedData);

  // Create workbook and add the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Generate filename with date
  const dateStr = format(new Date(), "yyyy-MM-dd");
  const fullFilename = `${filename}_${dateStr}.xlsx`;

  // Write and download the file
  XLSX.writeFile(wb, fullFilename);
}

/**
 * Export multiple sheets to a single Excel file
 * @param sheets Array of {name, data} objects
 * @param filename Base filename without extension
 */
export function exportMultiSheetExcel(
  sheets: Array<{ name: string; data: Record<string, unknown>[] }>,
  filename: string
): void {
  const wb = XLSX.utils.book_new();

  for (const sheet of sheets) {
    if (sheet.data && sheet.data.length > 0) {
      const ws = XLSX.utils.json_to_sheet(sheet.data);
      XLSX.utils.book_append_sheet(wb, ws, sheet.name);
    }
  }

  // Generate filename with date
  const dateStr = format(new Date(), "yyyy-MM-dd");
  const fullFilename = `${filename}_${dateStr}.xlsx`;

  // Write and download the file
  XLSX.writeFile(wb, fullFilename);
}

// PDF Export Interface
export interface PDFExportOptions {
  title: string;
  subtitle?: string;
  dateRange?: { start: string; end: string };
  orientation?: "portrait" | "landscape";
}

/**
 * Export data to PDF file
 * @param data Array of objects to export
 * @param filename Base filename without extension
 * @param options PDF export options
 */
export function exportToPDF<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  options: PDFExportOptions = { title: "Report" }
): void {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  const doc = new jsPDF({
    orientation: options.orientation || "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let yPosition = margin;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(options.title, margin, yPosition);
  yPosition += 8;

  if (options.subtitle) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(options.subtitle, margin, yPosition);
    yPosition += 6;
  }

  // Date range
  if (options.dateRange) {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Date Range: ${options.dateRange.start} to ${options.dateRange.end}`,
      margin,
      yPosition
    );
    yPosition += 6;
  }

  // Generated date
  doc.setFontSize(9);
  doc.text(
    `Generated: ${format(new Date(), "MMM dd, yyyy HH:mm")}`,
    margin,
    yPosition
  );
  yPosition += 10;

  // Reset text color
  doc.setTextColor(0, 0, 0);

  // Get headers from first data row
  const headers = Object.keys(data[0]);
  const tableData = data.map((row) =>
    headers.map((header) => {
      const value = row[header];
      if (typeof value === "number") {
        // Counts (orders, quantities) stay whole; anything fractional is money,
        // which must show exactly two decimals so the PDF matches the screen
        // instead of printing a bare float like "1,234.5".
        return Number.isInteger(value)
          ? value.toLocaleString("en-QA")
          : value.toLocaleString("en-QA", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
      }
      return String(value ?? "");
    })
  );

  // Generate table
  autoTable(doc, {
    startY: yPosition,
    head: [headers],
    body: tableData,
    theme: "striped",
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
    },
    bodyStyles: {
      fontSize: 7,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { left: margin, right: margin },
    styles: {
      cellPadding: 2,
      overflow: "linebreak",
    },
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    const footerY = doc.internal.pageSize.getHeight() - 10;
    doc.text(
      `Page ${i} of ${pageCount} | WeClean Car Wash`,
      pageWidth / 2,
      footerY,
      { align: "center" }
    );
  }

  // Generate filename with date
  const dateStr = format(new Date(), "yyyy-MM-dd");
  const fullFilename = `${filename}_${dateStr}.pdf`;

  // Save file
  doc.save(fullFilename);
}
